import React from 'react';

import {
  Box,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
} from '@mui/material';

import { DecisionIcon } from '../../../common/components/DecisionIcon';
import { Loading } from '../../../common/components/Loading';
import { Decision } from '../../../common/constants/decision';
import { IRuleResults } from '../interfaces/IRuleResults';
import RuleAccordion from './RuleAccordion';

export const RuleResultsView = ({
  data,
  isRidersTab,
  isGen2 = false,
  showConditions = true,
}: {
  data: {
    ruleResults: IRuleResults | null;
    fetchRuleResultsInProgress: boolean;
    fetchRuleResultsError: boolean | null;
  };
  isRidersTab?: boolean;
  isGen2?: boolean;
  showConditions?: boolean;
}) => {
  const { fetchRuleResultsInProgress, fetchRuleResultsError, ruleResults } =
    data;

  return (
    <Box>
      {fetchRuleResultsInProgress && (
        <Box width="100%" display="flex" justifyContent="center" p={3}>
          <Loading fullscreen={false} />
        </Box>
      )}

      {fetchRuleResultsError && (
        <Box width="100%" display="flex" justifyContent="center">
          <Typography variant="body2">Error getting rule results</Typography>
        </Box>
      )}

      {ruleResults?.error && (
        <Box width="100%" display="flex" justifyContent="center">
          <Typography variant="body2">{ruleResults.error}</Typography>
        </Box>
      )}

      {ruleResults && ruleResults.missingEvidences?.length > 0 && (
        <Box
          sx={{ marginTop: 4, display: 'flex', gap: 2 }}
          flexDirection={'column'}
        >
          <Typography variant="h6">Missing Evidences</Typography>
          <TableContainer component={Paper}>
            <Table sx={{ minWidth: 650 }} aria-label="Missing Evidence Table">
              <TableHead sx={{ backgroundColor: '#fafafa' }}>
                <TableRow>
                  <TableCell>Evidence name</TableCell>
                  <TableCell align="center">Decision</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {ruleResults &&
                  ruleResults.missingEvidences?.map(evidence => (
                    <TableRow key={evidence.evidenceName}>
                      <TableCell>
                        <Typography variant="subtitle2">
                          {evidence.evidenceName}
                        </Typography>
                      </TableCell>
                      <TableCell>
                        {evidence.decision && (
                          <DecisionIcon
                            decision={evidence.decision as Decision}
                          />
                        )}
                      </TableCell>
                    </TableRow>
                  ))}
              </TableBody>
            </Table>
          </TableContainer>
        </Box>
      )}

      {showConditions && ruleResults && !ruleResults.error && (
        <div style={{ marginTop: '16px' }}>
          {ruleResults.ruleSets.length > 0 ? (
            ruleResults.ruleSets.map((ruleSet, ruleSetIdx) => (
              <RuleAccordion
                key={ruleSetIdx}
                isRidersTab={isRidersTab}
                ruleSet={ruleSet}
              />
            ))
          ) : (
            <Box width="100%" textAlign="center" p={2}>
              <Typography variant="body2">
                {isRidersTab ? 'No riders data found.' : 'No rules found.'}
              </Typography>
            </Box>
          )}
        </div>
      )}

      {/* We dont show dx mapping on application that is gen2 or above */}
      {showConditions &&
        !isGen2 &&
        ruleResults &&
        ruleResults.dxTaxonomiesHit?.length > 0 && (
          <Box
            sx={{
              marginTop: 4,
              display: 'flex',
              flexDirection: 'column',
              gap: 2,
            }}
          >
            <Typography variant="h6">Dx Taxonomies Hit</Typography>
            <TableContainer component={Paper}>
              <Table sx={{ minWidth: 650 }} aria-label="Dx Taxonomies Table">
                <TableHead sx={{ backgroundColor: '#fafafa' }}>
                  <TableRow>
                    <TableCell sx={{ p: 1 }}>Dx Code</TableCell>
                    <TableCell sx={{ p: 1 }}>Taxonomy Names</TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {ruleResults.dxTaxonomiesHit.map((t, idx) => (
                    <TableRow key={idx} sx={{ borderBottom: 'none' }}>
                      <TableCell sx={{ p: 1, borderBottom: 'none' }}>
                        <Typography variant="subtitle2">{t.dxCode}</Typography>
                      </TableCell>
                      <TableCell sx={{ p: 1, borderBottom: 'none' }}>
                        <Typography variant="subtitle2">
                          {t.taxonomyNames.join(', ')}
                        </Typography>
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Box>
        )}
    </Box>
  );
};
